/**
 * Central configuration for the on-device photo post-processing pipeline
 * (face detection + pixelation, and timestamp overlay).
 *
 * Feature toggles follow the existing `VUE_APP_FEAT_*` convention (string
 * `'true'` flags, see Menu.vue / Tabs.vue). Each post-processing step can be
 * disabled independently in the code via these env vars.
 *
 * Tuning constants are kept here so behaviour can be adjusted in one place.
 */

const isFlagEnabled = (value) => value === 'true' || value === true;

// Detection-strength presets exposed to end users in the config modal. Each
// preset bundles a face-detection confidence threshold with a tiling grid (NxN)
// — the two knobs that trade detection reach against speed and false positives.
// Only the friendly label (standard/high/max) is ever shown to users; the
// numbers stay internal. `standard` is the default: a deliberately conservative
// single full-frame pass, since face pixelation is opt-in.
export const DETECTION_STRENGTH_PRESETS = {
  standard: { minDetectionConfidence: 0.5, detectionGrid: 1 },
  high: { minDetectionConfidence: 0.3, detectionGrid: 2 },
  max: { minDetectionConfidence: 0.15, detectionGrid: 3 },
};

export const DEFAULT_DETECTION_STRENGTH = 'standard';

// Resolves a strength key to its { minDetectionConfidence, detectionGrid } pair,
// falling back to the default preset for unknown/undefined values.
export function resolveDetectionStrength(strength) {
  return (
    DETECTION_STRENGTH_PRESETS[strength] ||
    DETECTION_STRENGTH_PRESETS[DEFAULT_DETECTION_STRENGTH]
  );
}

// Tuning constants for the processing pipeline.
const TUNING = {
  // Local paths (served from public/) — keeps processing fully offline.
  wasmPath: '/assets/mediapipe/wasm',
  // Default FaceDetector model (production). full-range detects faces further
  // away (e.g. across a room), which covers more real-world capture situations.
  modelAssetPath: '/assets/mediapipe/blaze_face_full_range.tflite',

  // Models bundled locally and selectable on the test page. full-range detects
  // faces that are further away (e.g. across a room) at the cost of a slightly
  // heavier model; short-range is best for close/selfie-range faces.
  models: [
    {
      key: 'shortRange',
      path: '/assets/mediapipe/blaze_face_short_range.tflite',
    },
    {
      key: 'fullRange',
      path: '/assets/mediapipe/blaze_face_full_range.tflite',
    },
  ],

  // Minimum confidence for a detection to be considered a face. Derived from the
  // default detection-strength preset so the code default matches what users get
  // before overriding it in the config modal.
  minDetectionConfidence:
    DETECTION_STRENGTH_PRESETS[DEFAULT_DETECTION_STRENGTH].minDetectionConfidence,

  // Tiled detection: the short-range model downscales its input to ~128px, so
  // distant/small faces vanish. Splitting the image into an NxN grid of
  // overlapping tiles and detecting each one (plus one full-frame pass) gives
  // each tile far more effective resolution, greatly improving the detection
  // of faces that are further away. Derived from the default detection-strength
  // preset — a single full-frame pass (1x1). The "high" preset raises it to 2x2
  // and "max" to 3x3 for distant faces (each adds 1 + N*N detect() calls).
  detectionGrid:
    DETECTION_STRENGTH_PRESETS[DEFAULT_DETECTION_STRENGTH].detectionGrid,
  // Overlap between adjacent tiles, as a fraction of the tile size, so faces
  // straddling a tile boundary still appear whole in at least one tile.
  detectionTileOverlap: 0.2,

  // Mosaic/pixelation block size, expressed as a fraction of the face box's
  // largest side (e.g. 0.12 => ~8 blocks across the face). Clamped to a
  // minimum pixel size below.
  pixelationBlockRatio: 0.12,
  pixelationMinBlockSize: 6,

  // Padding added around each detected face box, as a fraction of its size,
  // so the pixelation safely covers the whole head/hair.
  faceBoxPadding: 0.25,

  // Timestamp overlay styling.
  timestamp: {
    // dayjs localized format tokens: date + short time (e.g. "06/09/2026 2:32 PM").
    format: 'L LT',
    // Font size as a fraction of the image's smallest side.
    fontSizeRatio: 0.035,
    minFontSize: 14,
    fontFamily: 'sans-serif',
    textColor: '#ffffff',
    backgroundColor: 'rgba(0, 0, 0, 0.55)',
    // Margin from the bottom-right corner, as a fraction of the smallest side.
    marginRatio: 0.02,
    paddingRatio: 0.4, // padding around text, relative to font size
  },

  // Output encoding.
  outputType: 'image/jpeg',
  outputQuality: 0.85,
};

/**
 * Returns the resolved post-processing configuration.
 *
 * - `enabled`: master switch. When false, the pipeline is fully bypassed and
 *   MediaPipe is never loaded.
 * - `faceBlur` / `timestamp`: per-step switches (only meaningful when enabled).
 * - `testPage`: whether the dev-only test page is exposed in the main menu.
 */
export function getPhotoProcessingConfig() {
  const enabled = isFlagEnabled(process.env.VUE_APP_FEAT_PHOTO_PROCESSING);

  return {
    enabled,
    faceBlur: enabled && isFlagEnabled(process.env.VUE_APP_FEAT_PHOTO_FACE_BLUR),
    timestamp:
      enabled && isFlagEnabled(process.env.VUE_APP_FEAT_PHOTO_TIMESTAMP),
    testPage: isFlagEnabled(process.env.VUE_APP_FEAT_PHOTO_PROCESSING_TEST),
    tuning: TUNING,
  };
}

/**
 * Reads the "force" flags carried by a project. A project can impose the face
 * pixelation and/or the timestamp on every photo taken for its surveys, tickets
 * and ticket actions, whatever the user preferences are.
 *
 * Single place where the payload shape is read, so a change on the API side is
 * a one-line fix here.
 *
 * @param {object|null} project
 * @returns {{pixelateFaces: boolean, timestamp: boolean}}
 */
export function getProjectPhotoForcing(project) {
  return {
    pixelateFaces: Boolean(project && project.forcePhotoPixelateFaces),
    timestamp: Boolean(project && project.forcePhotoTimestamp),
  };
}

export { TUNING as PHOTO_PROCESSING_TUNING };
